import { FC, ReactNode } from 'react';
import { Upload, UploadProps } from 'antd';

import { StatusIcon } from './StatusIcon';
import { ErrorContent } from './ErrorContent';
import { UploadedFilesList } from './UploadedFilesList';
import type { UploadState, UploadTranslations } from '../types';
import styles from '../FileUploader.module.scss';

interface DropzoneAreaProps {
    uploadProps: UploadProps;
    state: UploadState;
    disabled?: boolean;
    dropzoneRef: React.RefObject<HTMLDivElement>;
    dropzoneTitle?: ReactNode;
    dropzoneSubtitle?: ReactNode;
    translations: UploadTranslations;
    onRetry: () => void;
}

export const DropzoneArea: FC<DropzoneAreaProps> = ({
    uploadProps,
    state,
    disabled,
    dropzoneRef,
    dropzoneTitle,
    dropzoneSubtitle,
    translations,
    onRetry,
}) => {
    const { visualState, progress, statusSubtitle, uploadedFiles } = state;

    const defaultTitle = dropzoneTitle ?? (
        <>
            <span className={styles.dropLink}>{translations.uploadClick}</span>{' '}
            {translations.uploadDrag}
        </>
    );

    const defaultSubtitle = dropzoneSubtitle ?? translations.supportedFormats;

    const renderContent = () => {
        if (visualState === 'error') {
            return (
                <ErrorContent
                    subtitle={statusSubtitle}
                    translations={translations}
                    onRetry={onRetry}
                />
            );
        }

        if (visualState === 'success') {
            return (
                <div className={styles.successMessage}>
                    <div className={styles.successTitle}>
                        {translations.uploadSuccess}
                    </div>
                    <UploadedFilesList
                        files={uploadedFiles}
                        allowRemove={false}
                        showEmptyState={false}
                        translations={translations}
                    />
                </div>
            );
        }

        return (
            <>
                {visualState === 'idle' && (
                    <span className={styles.dropTitle}>{defaultTitle}</span>
                )}
                <span className={styles.dropSubtitle}>
                    {statusSubtitle ?? defaultSubtitle}
                </span>
                <UploadedFilesList
                    files={uploadedFiles}
                    allowRemove={false}
                    showEmptyState={false}
                    translations={translations}
                />
            </>
        );
    };

    return (
        <Upload.Dragger
            {...uploadProps}
            className={styles.dropzoneArea}
            disabled={disabled}
        >
            <div className={styles.dropContent} ref={dropzoneRef}>
                <StatusIcon visualState={visualState} progress={progress} />
                {renderContent()}
            </div>
        </Upload.Dragger>
    );
};
